export const MINING_ECONOMY_VERSION = "commitment-v1";
export const MINING_SCALE = 1000000n;
export const MINING_WINDOW_MS = 86_400_000;
export const MINING_BUCKET_MS = 60_000;
const U64 = 18446744073709551615n;
/** A candidate for prospective release. Never modifies a purchased lot or published week. */
export const CANDIDATE_MINING_RULES = Object.freeze({
    version: "commitment-balanced-v1", controller: MINING_ECONOMY_VERSION,
    tiers: Object.freeze({
        COMMON: { baseLamports: "1000000", rawPerCore: "10000000", dailyRawTarget: "1237113402", baseVp: 1 },
        UNCOMMON: { baseLamports: "1500000", rawPerCore: "5000000", dailyRawTarget: "618556701", baseVp: 2 },
        RARE: { baseLamports: "2250000", rawPerCore: "2500000", dailyRawTarget: "309278351", baseVp: 4 },
        EPIC: { baseLamports: "3375000", rawPerCore: "1250000", dailyRawTarget: "154639176", baseVp: 8 },
        LEGENDARY: { baseLamports: "5062500", rawPerCore: "625000", dailyRawTarget: "77319588", baseVp: 16 },
    }),
    maximumDifficulty: "1000000000", recoveryHalfLifeMs: 3_600_000, maximumOrderCubes: 4096,
    maximumEventBonus: 6, nonFeaturedBonus: 1,
});
export function validateMiningRules(rules) {
    if (!rules || rules.controller !== MINING_ECONOMY_VERSION || typeof rules.version !== "string" || !/^[A-Za-z0-9_-]{1,96}$/.test(rules.version)
        || !rules.tiers || Object.keys(rules.tiers).sort().join() !== "COMMON,EPIC,LEGENDARY,RARE,UNCOMMON"
        || !Number.isSafeInteger(rules.maximumOrderCubes) || rules.maximumOrderCubes < 1 || rules.maximumOrderCubes > 4096
        || !Number.isSafeInteger(rules.recoveryHalfLifeMs) || rules.recoveryHalfLifeMs < 60_000 || rules.recoveryHalfLifeMs > MINING_WINDOW_MS
        || !Number.isSafeInteger(rules.maximumEventBonus) || rules.maximumEventBonus < 1 || rules.maximumEventBonus > 100
        || !Number.isSafeInteger(rules.nonFeaturedBonus) || rules.nonFeaturedBonus < 1 || rules.nonFeaturedBonus > rules.maximumEventBonus
        || units(rules.maximumDifficulty) < MINING_SCALE || units(rules.maximumDifficulty) > 1000000000n)
        throw Error("mining_rules_invalid");
    for (const tier of Object.values(rules.tiers)) {
        if (!tier || units(tier.baseLamports) < 1n || units(tier.rawPerCore) < 1000n || units(tier.rawPerCore) > 10000000n || units(tier.dailyRawTarget) < 1n
            || !Number.isSafeInteger(tier.baseVp) || tier.baseVp < 1 || tier.baseVp * rules.maximumEventBonus > 100)
            throw Error("mining_rules_invalid");
        if (units(tier.rawPerCore) * 8500n * MINING_SCALE / (10000n * units(rules.maximumDifficulty)) < 2n
            || miningScannerLamports(units(tier.baseLamports), units(rules.maximumDifficulty)) * BigInt(rules.maximumOrderCubes) > U64)
            throw Error("mining_rules_invalid");
    }
    return rules;
}
export function emptyMiningPressure(resourceId, now, migrationId = null) {
    time(now);
    return { resourceId, migrationId, rolling: [], reservations: [], outstandingCohorts: [], outstandingRaw: 0n,
        committedLifetimeRaw: 0n, realizedLifetimeMaterial: 0n, protocolBaselineMaterial: 0n, lastDifficulty: MINING_SCALE, updatedAt: now };
}
export function pressureAt(state, now) {
    time(now);
    if (now < state.updatedAt)
        throw Error("mining_clock_reversed");
    const cohorts = state.outstandingCohorts ?? [{ minute: null, raw: state.outstandingRaw }];
    const recent = cohorts.filter(row => row.minute !== null && (row.minute + 1) * MINING_BUCKET_MS > now - MINING_WINDOW_MS);
    const older = sum(cohorts.filter(row => row.minute === null || (row.minute + 1) * MINING_BUCKET_MS <= now - MINING_WINDOW_MS).map(row => row.raw));
    if (sum(cohorts.map(row => row.raw)) !== state.outstandingRaw)
        throw Error("mining_exposure_reconciliation_required");
    const held = new Map();
    // Expired holds have identical pressure/headroom semantics. Keep their amount,
    // not one row per elapsed second; original operation records retain recovery identity.
    for (const row of state.capacityHolds ?? []) {
        const expiry = row.quoteExpiresAt <= now ? 0 : row.quoteExpiresAt;
        held.set(expiry, (held.get(expiry) ?? 0n) + row.raw);
    }
    return { ...state, capacityHolds: [...held].map(([quoteExpiresAt, raw]) => ({ quoteExpiresAt, raw })), outstandingCohorts: [...(older ? [{ minute: null, raw: older }] : []), ...recent], rolling: state.rolling.filter(row => (row.minute + 1) * MINING_BUCKET_MS > now - MINING_WINDOW_MS),
        realized: (state.realized ?? []).filter(row => (row.minute + 1) * MINING_BUCKET_MS > now - MINING_WINDOW_MS),
        reservations: state.reservations.filter(row => row.expiresAt > now) };
}
export function miningExposure(state, now) {
    const current = pressureAt(state, now);
    const rollingRaw = sum(current.rolling.map(row => row.raw)), reservedRaw = sum(current.reservations.map(row => row.raw));
    const overlapRaw = sum(current.outstandingCohorts.filter(row => row.minute !== null).map(row => row.raw));
    const pendingCapacityRaw = sum((current.capacityHolds ?? []).filter(row => row.quoteExpiresAt <= now).map(row => row.raw));
    // Count the union: rolling paid demand plus older outstanding obligations. Recent outstanding hauls appear once.
    return { rollingRaw, reservedRaw, outstandingRaw: current.outstandingRaw, realized24HourMaterial: sum((current.realized ?? []).map(row => row.material)),
        pendingCapacityRaw,
        pressureRaw: rollingRaw + current.outstandingRaw - overlapRaw + reservedRaw + pendingCapacityRaw };
}
/** Immediate upward response; a one-hour half-life bounds downward recovery. No hidden sub-1 state. */
export function miningDifficulty(state, rule, rules, now) {
    const exposure = miningExposure(state, now);
    const wanted = clamp(MINING_SCALE + exposure.pressureRaw * MINING_SCALE / units(rule.dailyRawTarget), MINING_SCALE, units(rules.maximumDifficulty));
    const prior = clamp(state.lastDifficulty, MINING_SCALE, units(rules.maximumDifficulty));
    if (wanted >= prior)
        return wanted;
    const decay = Math.pow(0.5, (now - state.updatedAt) / rules.recoveryHalfLifeMs);
    const remaining = BigInt(Math.ceil(Number(prior - MINING_SCALE) * decay));
    const recovering = MINING_SCALE + remaining;
    return recovering > wanted ? recovering : wanted;
}
/** Exact ceil(base * sqrt(D)); no floating point lamport rounding. */
export function miningScannerLamports(base, difficulty) {
    const squared = base * base * difficulty;
    let result = integerSqrt(squared / MINING_SCALE);
    if (result * result * MINING_SCALE < squared)
        result++;
    return checked(result);
}
/** One quantity-integrated order. Stored Scanners cover cost only; they do not reserve future yield. */
export function quoteMiningOrder(state, tier, rules, cubeCount, now, ownedScanners = 0, paidRawPerCore = units(tier.rawPerCore)) {
    if (!Number.isSafeInteger(cubeCount) || cubeCount < 1 || cubeCount > rules.maximumOrderCubes
        || !Number.isSafeInteger(ownedScanners) || ownedScanners < 0 || ownedScanners > cubeCount)
        throw Error("mining_order_invalid");
    let virtual = pressureAt(state, now), scannerLamports = 0n, expectedRaw = 0n, minimumRaw = 0n, maximumRaw = 0n;
    let firstDifficulty = 0n, lastDifficulty = 0n;
    for (let index = 0; index < cubeCount; index++) {
        const difficulty = miningDifficulty(virtual, tier, rules, now);
        if (index === 0)
            firstDifficulty = difficulty;
        lastDifficulty = difficulty;
        const raw = positiveYield(paidRawPerCore * 6n * MINING_SCALE / (5n * difficulty));
        expectedRaw = checked(expectedRaw + raw);
        minimumRaw = checked(minimumRaw + positiveYield(paidRawPerCore * 8500n * MINING_SCALE / (10000n * difficulty)));
        maximumRaw = checked(maximumRaw + positiveYield(paidRawPerCore * 3n * 11500n * MINING_SCALE / (10000n * difficulty)));
        if (index >= ownedScanners)
            scannerLamports = checked(scannerLamports + miningScannerLamports(units(tier.baseLamports), difficulty));
        virtual = reserveMiningPressure(virtual, raw, now + 1, difficulty, now);
    }
    return { cubeCount, firstDifficulty, lastDifficulty, scannerLamports, expectedRaw, minimumRaw, maximumRaw };
}
export function reserveMiningPressure(state, raw, expiresAt, _difficulty, now) {
    checked(raw);
    time(expiresAt);
    if (expiresAt <= now || expiresAt > now + 900_000)
        throw Error("mining_reservation_expiry_invalid");
    const current = pressureAt(state, now), rows = [...current.reservations], index = rows.findIndex(row => row.expiresAt === expiresAt);
    if (index < 0)
        rows.push({ expiresAt, raw });
    else
        rows[index] = { expiresAt, raw: checked(rows[index].raw + raw) };
    // Reserving does not persist an elevated recovery baseline: abandoned quotes leave no permanent demand.
    return { ...current, reservations: rows, lastDifficulty: state.lastDifficulty, updatedAt: state.updatedAt };
}
export function releaseMiningPressure(state, raw, expiresAt, now) {
    const current = pressureAt(state, now);
    if (expiresAt <= now)
        return current;
    const row = current.reservations.find(item => item.expiresAt === expiresAt);
    if (!row || row.raw < raw)
        throw Error("mining_reservation_mismatch");
    return { ...current, reservations: current.reservations.map(item => item === row ? { ...item, raw: item.raw - raw } : item).filter(item => item.raw > 0n) };
}
export function commitMiningPressure(state, raw, fundedAt, now, tier, rules) {
    checked(raw);
    time(fundedAt);
    if (fundedAt > now)
        throw Error("mining_commitment_future");
    let current = pressureAt(state, now);
    const minute = Math.floor(fundedAt / MINING_BUCKET_MS), rolling = [...current.rolling];
    if ((minute + 1) * MINING_BUCKET_MS > now - MINING_WINDOW_MS) {
        const index = rolling.findIndex(row => row.minute === minute);
        if (index < 0)
            rolling.push({ minute, raw });
        else
            rolling[index] = { minute, raw: checked(rolling[index].raw + raw) };
    }
    const cohorts = [...current.outstandingCohorts], cohortMinute = (minute + 1) * MINING_BUCKET_MS > now - MINING_WINDOW_MS ? minute : null;
    const cohortIndex = cohorts.findIndex(row => row.minute === cohortMinute);
    if (cohortIndex < 0)
        cohorts.push({ minute: cohortMinute, raw });
    else
        cohorts[cohortIndex] = { minute: cohortMinute, raw: checked(cohorts[cohortIndex].raw + raw) };
    current = { ...current, rolling, outstandingCohorts: cohorts, outstandingRaw: checked(current.outstandingRaw + raw), committedLifetimeRaw: checked(current.committedLifetimeRaw + raw) };
    return { ...current, lastDifficulty: miningDifficulty(current, tier, rules, now), updatedAt: now };
}
/** Refining replaces RAW exposure with measured output. It does not create another mining commitment. */
export function realizeMiningPressure(state, raw, material, now, tier, rules, consumed) {
    checked(raw);
    checked(material);
    if (raw > state.outstandingRaw || material > raw * 20n)
        throw Error("mining_exposure_reconciliation_required");
    const current = pressureAt(state, now), minute = Math.floor(now / MINING_BUCKET_MS), realized = [...(current.realized ?? [])], index = realized.findIndex(row => row.minute === minute);
    if (index < 0)
        realized.push({ minute, material });
    else
        realized[index] = { minute, material: checked(realized[index].material + material) };
    let debit = raw;
    let outstandingCohorts = [...current.outstandingCohorts].sort((a, b) => (a.minute ?? -1) - (b.minute ?? -1)).map(row => {
        const consumed = row.raw < debit ? row.raw : debit;
        debit -= consumed;
        return { ...row, raw: row.raw - consumed };
    }).filter(row => row.raw > 0n);
    if (consumed) {
        if (sum(consumed.map(row => row.raw)) !== raw)
            throw Error("mining_exposure_reconciliation_required");
        outstandingCohorts = [...current.outstandingCohorts];
        for (const item of consumed) {
            const minute = item.minute !== null && (item.minute + 1) * MINING_BUCKET_MS > now - MINING_WINDOW_MS ? item.minute : null;
            const index = outstandingCohorts.findIndex(row => row.minute === minute);
            if (index < 0 || outstandingCohorts[index].raw < item.raw)
                throw Error("mining_exposure_reconciliation_required");
            outstandingCohorts[index] = { minute, raw: outstandingCohorts[index].raw - item.raw };
        }
        outstandingCohorts = outstandingCohorts.filter(row => row.raw > 0n);
    }
    return { ...current, realized, outstandingCohorts, ...(tier && rules ? { lastDifficulty: miningDifficulty(current, tier, rules, now), updatedAt: now } : {}), outstandingRaw: state.outstandingRaw - raw,
        realizedLifetimeMaterial: checked(state.realizedLifetimeMaterial + material) };
}
export function effectiveMiningVp(tier, eventBonus, rules) {
    if (!Number.isSafeInteger(eventBonus) || eventBonus < 1 || eventBonus > rules.maximumEventBonus)
        throw Error("mining_vp_bonus_invalid");
    const rate = tier.baseVp * eventBonus;
    if (!Number.isSafeInteger(rate) || rate > 100)
        throw Error("mining_vp_rate_invalid");
    return rate;
}
function integerSqrt(value) { if (value < 2n)
    return value; let x = value, next = (x + 1n) / 2n; while (next < x) {
    x = next;
    next = (x + value / x) / 2n;
} return x; }
function positiveYield(value) { return value > 0n ? value : 1n; }
function clamp(value, min, max) { return value < min ? min : value > max ? max : value; }
function sum(values) { return checked(values.reduce((total, value) => total + value, 0n)); }
function checked(value) { if (value < 0n || value > U64)
    throw Error("mining_amount_invalid"); return value; }
function units(value) { if (typeof value !== "string" || !/^(0|[1-9][0-9]{0,19})$/.test(value))
    throw Error("mining_units_invalid"); return checked(BigInt(value)); }
function time(value) { if (!Number.isSafeInteger(value) || value < 0)
    throw Error("mining_timestamp_invalid"); }
